<template>
  <BaseModal
    :id="id"
    :modalClass="`modal-lg`"
    :showCloseButton="true"
    @onClose="handleClose"
  >
    <template #header>
      <div
        v-if="selectedApp"
        class="d-flex justify-content-between align-items-md-center w-100"
        style="height: 30px;">
        <div
          class="d-flex justify-content-start align-items-md-center"
          style="min-width: 170px;">
          <span class="mr-2">
            <strong style="font-size: 18px;">{{ $t(selectedApp.translate + '.fullname') }}</strong>
          </span>
          <span>
            <div
              :style="'background:' + $t(selectedApp.translate + '.bgcolor')"
              class="d-flex justify-content-center align-items-center"
              style="width: 30px; height: 30px;"
            >
              <i
                :class="$t(selectedApp.translate + '.icon')"
                style="font-size: 15px;color: #fff;"/>
            </div>
          </span>
        </div>
        <div
          v-if="!loading && hasFeeAnyModule"
          class="d-flex justify-content-end align-items-center w-100">
          <template v-if="!device.isMobile">
            <div
              v-if="isFreeTrialLive"
              class="mr-3 text-success"
              style="font-size: 15px;">
              {{ $t('apps.installapp.message.freetrialdaysleft', { days: freeTrialDaysLeft }) }}
            </div>
            <span
              v-else-if="isBeforeEffectDate"
              class="mr-2 text-success">
              {{ $t('apps.installapp.message.startchargeapps') }}
            </span>
          </template>
          <div style="font-size: 15px;">
            <span :class="[(isFreeTrialLive || isBeforeEffectDate) && 'text-decoration-line-through']">
              {{ $t('apps.installapp.message.totalprice', { price: totalPrice }) }}
            </span>
          </div>
        </div>
      </div>
    </template>
    <template #body>
      <div
        v-if="selectedApp"
        class="container-body">
        <BaseLoading v-if="loading"/>
        <BaseTable
          v-else
          class="custom-table"
          tableClass="table-bordered"
          hidePagination
        >
          <template #thead>
            <thead>
              <tr>
                <th>{{ $t('apps.installapp.message.module') }}</th>
                <th>{{ $t('message.description') }}</th>
                <th class="text-center">{{ $t('apps.installapp.message.pricepermonth') }}</th>
                <th class="text-center">{{ $t('apps.installapp.message.installuninstallstatus') }}</th>
              </tr>
            </thead>
          </template>
          <template #tbody>
            <tbody>
              <tr
                v-for="(module, index) in availableModules"
                :key="index"
              >
                <td>
                  {{ $t(module.translate) }}
                </td>
                <td>
                  <a
                    v-if="module.learn_more_link"
                    :href="module.learn_more_link"
                    target="_blank">
                    {{ $t('button.learnmore') }}
                    <i
                      class="fas fa-external-link-alt"
                      style="font-size: 11px;"/>
                  </a>
                </td>
                <td
                  v-if="module.ignoreRow == null"
                  :rowspan="module.group + 1"
                  :class="['text-center', isFreeTrialLive && 'text-decoration-line-through']"
                  style="border: 1px solid #dee2e6;"
                >
                  {{ module.priceText }}
                </td>
                <td
                  v-if="module.ignoreRow == null"
                  :rowspan="module.group + 1"
                  class="text-center"
                >
                  <div class="d-flex flex-column justify-content-center align-items-center">
                    <vs-tooltip
                      v-if="switchDisabledValues[index] === true"
                      :text="$t('message.disabledinhrs', { number: switchDisabledTimeLeftInHrs[index] })"
                      position="top"
                      style="float: right;margin-left: 5px;">
                      <BaseSwitch
                        :value="switchValues[index]"
                        :disabled="switchDisabledValues[index] || isSubmitting"
                        color="#A3CA76"
                        @change="handleCheckBoxValueChange($event, index)"
                      />
                    </vs-tooltip>
                    <BaseSwitch
                      v-else
                      :value="switchValues[index]"
                      :disabled="switchDisabledValues[index] || isSubmitting"
                      color="#A3CA76"
                      @change="handleCheckBoxValueChange($event, index)"
                    />
                    <span
                      v-if="isFreeTrialLive && module.hasPrice"
                      class="text-success">
                      {{ $t('apps.installapp.message.freetrialdaysleftshort', { days: freeTrialDaysLeft }) }}
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </BaseTable>
      </div>
    </template>
    <template #footer>
      <div
        v-if="selectedApp"
        class="container-footer w-100">
        <div class="d-flex justify-content-between w-100">
          <div class="d-flex flex-row align-items-center">
            <template v-if="installedModuleIds.length > 0">
              <vs-tooltip
                v-if="maxDisabledUninstallInHrs > 0"
                :text="$t('message.disableduninstallinhrs', { number: maxDisabledUninstallInHrs })"
                position="top"
                style="float: right;margin-left: 5px;">
                <button
                  :disabled="loading || isSubmitting || maxDisabledUninstallInHrs > 0"
                  type="button"
                  class="btn btn-danger"
                  @click="handleUninstall"
                >
                  {{ $t('apps.installapp.button.unintallapp') }}
                </button>
              </vs-tooltip>
              <button
                v-else
                :disabled="loading || isSubmitting || maxDisabledUninstallInHrs > 0"
                type="button"
                class="btn btn-danger"
                @click="handleUninstall"
              >
                {{ $t('apps.installapp.button.unintallapp') }}
              </button>
            </template>
            <div
              v-else-if="hasFeeAnyModule"
              class="d-flex align-items-md-center"
            >
              <a
                class="btn no-bg p-0"
                href="https://www.treesoft.io/th/pricing"
                target="_blank"
              >
                {{ $t('message.treesoftpricing') }}
                <i
                  class="fas fa-external-link-alt"
                  style="font-size: 11px;"/>
              </a>
            </div>
          </div>
          <div
            class="d-flex flex-row align-items-center"
            style="gap: 10px;">
            <div
              v-if="isSubmitting"
              class="d-flex flex-row align-items-center"
              style="gap: 10px;">
              <CircleProgress
                :percent="loadingPercent"
                :size="25"
                :viewport="true"
                :showPercent="false"
                :borderBgWidth="3"
                :borderWidth="3"
                fillColor="#A3CA76"
              />
              <div>
                {{ loadingButton }}
              </div>
            </div>
            <template v-else>
              <button
                :disabled="isSubmitting"
                type="button"
                class="btn btn-light"
                @click="handleClose"
              >
                {{ $t('button.cancel') }}
              </button>
              <button
                :disabled="loading || isSubmitting || isAllModulesInactive"
                type="button"
                class="btn btn-web"
                @click="handleApply"
              >
                {{ applyButton }}
              </button>
            </template>
          </div>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import moment from 'moment'
import { HAS_FEE_APP_MODULES } from '../../helpers/enum'
import CircleProgress from '../custom/CircleProgress.vue'

export default {
  components: {
    CircleProgress,
  },

  props: {
    id: String,
    selectedApp: Object,
    installed: Array,
    pricing: Object,
    isSubmitting: Boolean,
    freeTrialDaysLeft: Number,
    isFreeTrialLive: Boolean,
    loadingPercent: Number,
  },

  data () {
    return {
      loading: true,
      switchValues: [],
      switchDisabledValues: [],
      switchDisabledTimeLeftInHrs: [],
      maxDisabledUninstallInHrs: 0,
      isAllModulesInactive: false,
      totalPrice: 0,
    }
  },

  computed: {
    appId () {
      return this.selectedApp && this.selectedApp.app_id
    },
    availableModules () {
      const appId = this.appId
      const modules = this.selectedApp
        ? this.selectedApp.modules
          .map((module) => {
            return {
              ...module,
              price: (() => {
                let price = null
                Object.keys(this.pricing).forEach((key) => {
                  if (key === `APPID${appId}_MODULEID${module.module_id}`) {
                    price = this.pricing[key]
                  }
                })
                return price
              })(),
            }
          })
        : []

      let groupPrice = 0
      let groupNumber = 0
      let groupCount = null
      let groupIndex = [0, 0]
      for (let i = 0; i < modules.length; i++) {
        const module = modules[i]
        if (module.group != null) {
          groupNumber = module.group
          groupCount = 1
          groupPrice = module.price
          groupIndex = i
        } else if (groupCount >= 1 && groupCount <= groupNumber) {
          groupCount++
          groupPrice += module.price
          modules[groupIndex].price = groupPrice
          modules[i].price = null
          modules[i].ignoreRow = true
        }
      }
      return modules.map((module) => {
        return {
          ...module,
          priceText: module.price != null
            ? this.getCurrencyWithNumber(this.numberel(Math.round(module.price), '0,0.00'))
            : '',
          hasPrice: module.price != null,
          recommended: this.getRecommendedInstallModule(appId, module.module_id),
        }
      })
    },
    installedModuleIds () {
      const app_id = this.selectedApp && this.selectedApp.app_id
      const item = this.installed.find((item) => item.app_id === app_id)
      return item
        ? item.modules.map((item) => item.module_id)
        : []
    },
    applyButton () {
      return this.hasAnyModuleInstalled
        ? this.$t('apps.installapp.button.editapp')
        : this.$t('apps.installapp.button.installapp')
    },
    hasFeeAnyModule () {
      return this.availableModules.some((module) => module.price != null)
    },
    isBeforeEffectDate () {
      const effectDate = moment('2024-04-01')
      const now = moment()
      return effectDate.diff(now) > 0
    },
    hasAnyModuleInstalled() {
      return this.installedModuleIds.length > 0
    },
    loadingButton() {
      return this.hasAnyModuleInstalled
        ? this.$t('message.loading')
        : this.$t('message.installing')
    },
  },

  watch: {
    selectedApp: {
      handler: async function () { // mounted
        this.loading = true
        await this.$store.dispatch('setLocalStorageShopInfo')
        this.initialSwitchValues()
        this.loading = false
      },
      deep: true,
    },
  },

  methods: {
    handleClose () {
      this.$emit('onClose')
    },
    handleCheckBoxValueChange ($event, index) {
      const module = this.availableModules[index]
      if (module.group != null) {
        this.switchValues[index] = $event
        for (let i = 1; i <= module.group; i++) {
          this.switchValues[index + i] = $event
        }
      } else {
        this.switchValues[index] = $event
      }
      this.setAllModuleInactive()
      this.setTotalPrice()
    },
    initialSwitchValues () {
      this.switchValues = []
      this.availableModules.forEach((module, index) => {
        const isInstalledModule = this.installedModuleIds.includes(module.module_id)
        const hasInstalledBefore = this.hasAnyModuleInstalled
        const switchValue = (() => {
          if (isInstalledModule) {
            return true
          } else if (module.recommended && !hasInstalledBefore) {
            return true
          }
          return false
        })()

        const isModuleInstalled = (() => {
          const app = this.installed.find((appItem) => appItem.app_id === this.appId)
          const moduleItem = app && app.modules.find((moduleItem) => moduleItem.module_id === module.module_id)
          return !!moduleItem
        })()

        const createdAt = (() => {
          const app = this.installed.find((appItem) => appItem.app_id === this.appId)
          const moduleItem = app && app.modules.find((moduleItem) => moduleItem.module_id === module.module_id)
          return moduleItem && moduleItem.created_at
        })()
        const DISABLED_IN_HRS = 24
        const diffInHrs = this.moment(moment()).diff(createdAt, 'hours')
        const switchDisabledValue = (() => {
          if (switchValue === true && isModuleInstalled && diffInHrs < DISABLED_IN_HRS && !this.isFreeTrialLive) {
            return true
          }
          return false
        })()

        this.switchValues[index] = switchValue
        this.switchDisabledValues[index] = switchDisabledValue
        this.switchDisabledTimeLeftInHrs[index] = switchDisabledValue ? DISABLED_IN_HRS - diffInHrs : 0
      })
      this.setAllModuleInactive()
      this.setTotalPrice()
      this.maxDisabledUninstallInHrs = Math.max(...this.switchDisabledTimeLeftInHrs)
    },
    handleApply () {
      const appId = this.selectedApp.app_id
      const activeModuleIds = this.getSwitchOnModuleIds()
      this.$emit('onApply', {
        appId,
        moduleIds: activeModuleIds,
      })
    },
    handleUninstall () {
      const appId = this.selectedApp.app_id
      this.$emit('onUninstall', {
        appId,
      })
    },
    getSwitchOnModuleIds () {
      return this.availableModules.reduce((acc, cur, idx) => {
        if (this.switchValues[idx] == true) {
          acc.push(cur.module_id)
        }
        return acc
      }, [])
    },
    getRecommendedInstallModule (appId, moduleId) {
      const hasFee = HAS_FEE_APP_MODULES.some((item) => item.app_id === appId && item.module_id === moduleId)
      return !!(this.isFreeTrialLive || !hasFee)
    },
    setAllModuleInactive () {
      this.isAllModulesInactive = this.switchValues.every((v) => v == false)
    },
    setTotalPrice () {
      const result = this.switchValues.reduce((acc, cur, idx) => {
        if (cur == true) {
          acc += this.availableModules[idx].price
        }
        return acc
      }, 0)
      this.totalPrice = this.getCurrencyWithNumber(this.numberel(Math.round(result), '0,0.00'))
    },
  },
}
</script>

<style scoped>
.custom-table .table.dataTable tr:hover {
  background: none;
}

.text-decoration-line-through {
  text-decoration: line-through !important;
}
</style>



// WEBPACK FOOTER //
// src/components/base/BaseModalInstallApp.vue